import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { Button, LinearProgress } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Can } from '../../components/Can';
import NewTicketModal from '../../components/NewTicketModal';
import KanbanBoard from './KanbanBoard';
import { buildLanes, filterTickets, getOpportunityValue, money } from './kanbanUtils';
import './kanban.css';

const emptyFilters = { search: '', start: '', end: '', userId: '' };
const Kanban = () => {
  const { user, socket } = useContext(AuthContext);
  const history = useHistory();
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [laneOrder, setLaneOrder] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [newTicket, setNewTicket] = useState(false);
  const pageRef = useRef(1);
  const requestRef = useRef(0);
  const loadingRef = useRef(false);
  const busyRef = useRef(false);
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('sortOrder') || 'lastMessageTime');
  const queueIds = JSON.stringify((user.queues || []).map(queue => queue.UserQueue?.queueId ?? queue.id));

  const fetchTickets = useCallback(async (append = false) => {
    if (append && loadingRef.current) return;
    const request = ++requestRef.current;
    loadingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const page = append ? pageRef.current + 1 : 1;
      const [response, tagResponse] = await Promise.all([
        api.get('/ticket/kanban', { params: { queueIds, pageNumber: page } }),
        append ? Promise.resolve(null) : api.get('/tag/kanban/'),
      ]);
      const { data } = response;
      if (request !== requestRef.current) return;
      if (tagResponse) setTags(tagResponse.data.lista || []);
      const incoming = Array.isArray(data.tickets) ? data.tickets : [];
      setTickets(previous => append ? Array.from(new Map([...previous, ...incoming].map(ticket => [ticket.id, ticket])).values()) : incoming);
      setHasMore(Boolean(data.hasMore) && incoming.length > 0);
      pageRef.current = page;
    } catch (err) {
      if (request === requestRef.current) setError('Não foi possível atualizar o Kanban. Os dados anteriores foram mantidos. Tente novamente.');
    } finally {
      if (request === requestRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, [queueIds]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const initialize = async () => {
      try {
        const order = await getKanbanLaneOrder();
        if (!active) return;
        setLaneOrder(order || []);
        await fetchTickets();
      } catch (err) {
        if (active) { setError('Não foi possível carregar as etapas. Atualize a página para tentar novamente.'); setLoading(false); }
      }
    };
    initialize();
    return () => { active = false; requestRef.current += 1; };
  }, [fetchTickets]);

  useEffect(() => {
    if (user.profile !== 'admin') return;
    let active = true;
    api.get('/users/list').then(({ data }) => { if (active) setUsers(Array.isArray(data) ? data : data.users || []); }).catch(() => {});
    return () => { active = false; };
  }, [user.profile]);

  useEffect(() => {
    if (!socket) return;
    let timer;
    const refresh = data => {
      if (!['create', 'update', 'delete'].includes(data.action) || busyRef.current) return;
      // Keep loaded pages and open dialogs stable; an explicit refresh reconciles additions.
      if (data.action === 'delete' && (data.ticketId || data.ticket?.id)) {
        setTickets(previous => previous.filter(ticket => String(ticket.id) !== String(data.ticketId || data.ticket.id)));
      } else if (data.ticket?.id) {
        setTickets(previous => previous.map(ticket => ticket.id === data.ticket.id ? { ...ticket, ...data.ticket } : ticket).filter(ticket => ['open', 'pending'].includes(ticket.status)));
      }
      if (pageRef.current === 1) {
        clearTimeout(timer);
        timer = setTimeout(() => { if (!loadingRef.current && !busyRef.current) fetchTickets(); }, 1200);
      }
    };
    const event = `company-${user.companyId}-ticket`;
    const messageEvent = `company-${user.companyId}-appMessage`;
    socket.on(event, refresh);
    socket.on(messageEvent, refresh);
    return () => { clearTimeout(timer); socket.off(event, refresh); socket.off(messageEvent, refresh); };
  }, [socket, user.companyId, fetchTickets]);

  useEffect(() => { localStorage.setItem('sortOrder', sortOrder); }, [sortOrder]);
  const invalidDates = filters.start && filters.end && filters.start > filters.end;
  const visible = useMemo(() => filterTickets(tickets, filters), [tickets, filters]);
  const lanes = useMemo(() => buildLanes(tags, visible, sortOrder, laneOrder), [tags, visible, sortOrder, laneOrder]);
  const total = visible.reduce((sum, ticket) => sum + getOpportunityValue(ticket), 0);
  const unread = visible.filter(ticket => Number(ticket.unreadMessages) > 0).length;
  const pending = visible.filter(ticket => ticket.status === 'pending').length;
  const changeFilter = key => event => {
    const value = event.target.value;
    setFilters(previous => ({ ...previous, [key]: value }));
  };

  const updateTicket = updated => setTickets(previous => previous.map(ticket => ticket.contact?.id === updated.contact?.id ? { ...ticket, contact: { ...ticket.contact, extraInfo: updated.contact.extraInfo } } : ticket));
  const moveCard = async (ticketId, targetLane) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);
      if (targetLane !== 'lane0') await api.put(`/ticket-tags/${ticketId}/${targetLane}`);
      const kanbanIds = new Set(tags.map(tag => String(tag.id)));
      const targetTag = tags.find(tag => String(tag.id) === targetLane);
      setTickets(previous => previous.map(ticket => ticket.id === ticketId ? { ...ticket, tags: [...(ticket.tags || []).filter(tag => !kanbanIds.has(String(tag.id))), ...(targetTag ? [targetTag] : [])] } : ticket));
      toast.success('Etapa atualizada.');
    } catch (err) {
      toast.error('Não foi possível concluir a mudança de etapa. Confira o cartão antes de tentar novamente.');
      await fetchTickets();
    } finally { busyRef.current = false; setBusy(false); }
  };
  const reorderLanes = async (source, destination) => {
    if (user.profile !== 'admin' || busyRef.current) return;
    const order = lanes.map(lane => lane.id);
    const [moved] = order.splice(source, 1);
    order.splice(destination, 0, moved);
    busyRef.current = true;
    setBusy(true);
    try { await setKanbanLaneOrder(order); setLaneOrder(order); toast.success('Ordem das etapas atualizada.'); }
    catch (err) { toast.error('Não foi possível salvar a ordem das etapas. Tente novamente.'); }
    finally { busyRef.current = false; setBusy(false); }
  };

  return <main className="zk-page">
    <header className="zk-page-header"><div><h1>Kanban de oportunidades</h1><p>Acompanhe seus atendimentos, da primeira conversa ao próximo passo.</p></div><Button className="zk-refresh" startIcon={<RefreshIcon />} onClick={() => fetchTickets()} disabled={loading || busy}>Atualizar</Button></header>
    <section className="zk-metrics" aria-label="Resumo dos cartões filtrados">
      <div><span>Valor em negociação</span><strong>{money(total)}</strong><small>Somatório dos valores dos cartões</small></div>
      <div><span>Oportunidades no quadro</span><strong>{visible.length}</strong><small>Atendimentos abertos e pendentes</small></div>
      <div><span>Valor médio estimado</span><strong>{money(visible.length ? total / visible.length : 0)}</strong><small>Valor total dividido pelos cartões</small></div>
      <div><span>Com mensagens não lidas</span><strong>{unread}</strong><small>Atendimentos para acompanhar</small></div>
      <div><span>Aguardando atendimento</span><strong>{pending}</strong><small>Cartões com status pendente</small></div>
    </section>
    <section className="zk-toolbar" aria-label="Filtros do Kanban">
      <div className="zk-toolbar-top"><label className="zk-search"><SearchIcon fontSize="small" /><input aria-label="Buscar no Kanban" placeholder="Buscar cliente, telefone, mensagem ou ticket" value={filters.search} onChange={changeFilter('search')} /></label><label className="zk-field zk-sort">Ordenar por<select value={sortOrder} onChange={event => setSortOrder(event.target.value)}><option value="lastMessageTime">Última atualização</option><option value="valorDesc">Maior valor</option><option value="ticketNumber">Número do ticket</option></select></label></div>
      <div className="zk-toolbar-bottom"><div className="zk-filter-fields">
        {user.profile === 'admin' && <label className="zk-field">Atendente<select value={filters.userId} onChange={changeFilter('userId')}><option value="">Todos os atendentes</option>{users.map(person => <option key={person.id} value={String(person.id)}>{person.name}</option>)}</select></label>}
        <label className="zk-field">Criado a partir de<input type="date" value={filters.start} onChange={changeFilter('start')} aria-invalid={Boolean(invalidDates)} /></label>
        <label className="zk-field">Criado até<input type="date" value={filters.end} onChange={changeFilter('end')} aria-invalid={Boolean(invalidDates)} /></label>
        {Object.values(filters).some(Boolean) && <button className="zk-clear" onClick={() => setFilters(emptyFilters)}>Limpar filtros</button>}
      </div><div className="zk-actions"><Can role={user.profile} perform="dashboard:view" yes={() => <Button className="zk-secondary" startIcon={<ViewColumnIcon />} onClick={() => history.push('/tagsKanban')}>Gerenciar etapas</Button>} /><Button className="zk-primary" startIcon={<AddIcon />} onClick={() => setNewTicket(true)}>Novo ticket</Button></div></div>
      {invalidDates && <p className="zk-error" role="alert">A data final precisa ser igual ou posterior à data inicial.</p>}
    </section>
    <div className="zk-board-info"><p>{visible.length} de {tickets.length} cartões carregados · Totais e filtros consideram estes cartões.{hasMore && ' Há mais atendimentos disponíveis.'}</p><span>Arraste pela alça para mover</span></div>
    {error && <div className="zk-error zk-error-box" role="alert">{error}</div>}
    {(loading || busy) && <div className="zk-loading" role="status"><LinearProgress /><span>{busy ? 'Salvando alteração…' : 'Carregando atendimentos…'}</span></div>}
    {!loading && !error && !visible.length && <div className="zk-no-results" role="status">{tickets.length ? 'Nenhum atendimento corresponde aos filtros.' : 'Nenhum atendimento aberto ou pendente encontrado.'}</div>}
    <KanbanBoard lanes={lanes} onCardMove={moveCard} onLaneReorder={reorderLanes} updateTicket={updateTicket} isAdmin={user.profile === 'admin'} busy={busy || loading} />
    {hasMore && <div className="zk-load-more"><Button className="zk-secondary" onClick={() => fetchTickets(true)} disabled={loading || busy}>Carregar mais atendimentos</Button></div>}
    <NewTicketModal modalOpen={newTicket} onClose={ticket => { setNewTicket(false); if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`); }} />
  </main>;
};

export default Kanban;
